import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ScrollText, RefreshCw, Search, Download, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { downloadCsv } from "@/lib/download";

export const AUDIT_GROUPS = [
  ["", "All"], ["auth", "Sign-ins"], ["billing", "Billing"], ["share", "Sharing"], ["team", "Team"],
  ["investors", "Investors"], ["docs", "Documents"], ["fund", "Funds"],
];
export const AUDIT_COLORS = {
  auth: "bg-sky-100 text-sky-800", billing: "bg-amber-100 text-amber-800",
  share: "bg-emerald-100 text-emerald-800", team: "bg-violet-100 text-violet-800",
  investors: "bg-teal-100 text-teal-800", docs: "bg-stone-200 text-stone-800",
  fund: "bg-indigo-100 text-indigo-800", admin: "bg-rose-100 text-rose-800",
};

export function AuditEventPill({ event }) {
  return (
    <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${AUDIT_COLORS[(event || "").split(".")[0]] || "bg-slate-100 text-slate-700"}`}>
      {event}
    </span>
  );
}

export function AuditTrailCard({ company }) {
  const [rows, setRows] = useState(null);
  const [group, setGroup] = useState("");
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const base = `/companies/${company?.id}/audit-log`;

  const load = (g = group, query = q) =>
    api.get(base, { params: { event: g, q: query } })
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load the activity log"));
  useEffect(() => { if (company?.id) load("", ""); }, [company?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    setExporting(true);
    try { await downloadCsv(`${base}.csv`, { event: group, q }, "tenx-audit-log.csv"); toast.success("Activity log exported"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <Card className="rounded-xl border bg-card p-6" data-testid="workspace-audit-card">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-primary" />
          <div>
            <h2 className="h-display text-lg font-semibold text-foreground">Activity log</h2>
            <p className="text-xs text-muted-foreground">Who signed in, shared a deck, invited a teammate or changed billing. Kept for 180 days.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => load()} data-testid="workspace-audit-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 text-xs" disabled={exporting || !rows?.length} onClick={exportCsv} data-testid="workspace-audit-export">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export CSV
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {AUDIT_GROUPS.map(([g, label]) => (
          <button key={label} type="button" onClick={() => { setGroup(g); load(g); }} data-testid={`workspace-audit-filter-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${group === g ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
        <form className="ml-auto flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people or details…" className="h-8 w-52 text-xs" data-testid="workspace-audit-search" />
          <Button type="submit" variant="outline" size="sm" className="h-8 px-2" data-testid="workspace-audit-search-btn"><Search className="h-3.5 w-3.5" /></Button>
        </form>
      </div>
      {rows === null ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground" data-testid="workspace-audit-empty">Nothing here yet. Sign-ins, shares and billing changes will show up as they happen.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="py-2 pr-4">When</th><th className="py-2 pr-4">Event</th><th className="py-2 pr-4">Who</th><th className="py-2">Detail</th>
            </tr></thead>
            <tbody data-testid="workspace-audit-rows">
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60" data-testid="workspace-audit-row">
                  <td className="whitespace-nowrap py-2 pr-4 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="py-2 pr-4"><AuditEventPill event={r.event} /></td>
                  <td className="max-w-48 truncate py-2 pr-4 text-foreground" title={r.actor || ""}>{r.actor || "-"}</td>
                  <td className="max-w-md truncate py-2 text-muted-foreground" title={r.detail || ""}>{r.detail || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
